import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Clock, User, Tag, AlertCircle, CheckCircle, XCircle, GitBranch } from 'lucide-react';
import { useNotify } from 'react-admin';
import { getDocumentApprovalInfo, getDocumentById, getDocumentVersionsByDocumentId, replaceApproverWithDeputy } from '../../../services/documentService.ts';
import { getAllUsers } from '../../../services/userService.ts';
import { Document, DocumentStatus } from '../../../types/document.ts';

export default function MytaskView() {
  const { id } = useParams(); // Document ID from URL
  const navigate = useNavigate();
  const notify = useNotify();

  const [document, setDocument] = useState<Document | undefined>();
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState<string | null>(null);
  const [userNameMap, setUserNameMap] = useState<Record<string, string>>({});
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [showDeputyModal, setShowDeputyModal] = useState(false);
  const [selectedStepInstanceId, setSelectedStepInstanceId] = useState<string>('');
  const [selectedFromUserId, setSelectedFromUserId] = useState<string>('');
  const [selectedDeputyUserId, setSelectedDeputyUserId] = useState<string>('');
  const [deputyComment, setDeputyComment] = useState('');
  const [savingDeputy, setSavingDeputy] = useState(false);

  useEffect(() => {
    if (id) {
      const loadDocument = async () => {
        try {
          const [doc, users, approvalRecords, versions] = await Promise.all([
            getDocumentById(id),
            getAllUsers(),
            getDocumentApprovalInfo(id),
            getDocumentVersionsByDocumentId(id),
          ]);

          const map: Record<string, string> = {};
          (users || []).forEach((user: any) => {
            const userId = user?.id ?? user?.Id ?? user?.userId;
            const userName = user?.userName ?? user?.username ?? user?.name;
            if (userId != null && userName) {
              map[String(userId)] = String(userName);
            }
          });
          setUserNameMap(map);
          setAllUsers(users || []);

          const enrichedDoc: Document = {
            ...doc,
            versions: versions.length > 0 ? versions : (doc.versions || []),
            approvalHistory: approvalRecords.length > 0 ? approvalRecords : (doc.approvalHistory || []),
          };

          setDocument(enrichedDoc as any);
          setLoading(false);
        } catch (error: any) {
          console.error('Failed to load document:', error);
          setError(error.message || 'Failed to load document');
          setLoading(false);
        }
      };
      loadDocument();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading document...</div>
        </div>
      </div>
    );
  }

  if (error || !document) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <div className="text-red-600 font-semibold mb-2">Failed to load document</div>
          <div className="text-gray-600 mb-4">{error || 'Document not found'}</div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: DocumentStatus) => {
    switch (status) {
      case 'Draft':
        return 'bg-gray-100 text-gray-700';
      case 'Review':
        return 'bg-yellow-100 text-yellow-700';
      case 'Approved':
        return 'bg-green-100 text-green-700';
      case 'Obsolete':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusIcon = (status: DocumentStatus) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'Review':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'Obsolete':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '-';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const getUserDisplayName = (rawValue?: string) => {
    if (!rawValue) return '-';
    const normalized = String(rawValue);
    return userNameMap[normalized] || normalized;
  };

  const getOwnerDisplayName = () => {
    const ownerName = (document as any)?.ownerName;
    if (ownerName) return String(ownerName);
    const ownerId = (document as any)?.ownerId ?? (document as any)?.owner;
    if (!ownerId) return '-';
    return getUserDisplayName(String(ownerId));
  };

  const getDisplayTags = () => {
    const source = (document as any)?.tags;
    if (Array.isArray(source)) {
      return source
        .map((item: any) => {
          if (typeof item === 'string') return item;
          return item?.name ?? item?.tagName ?? item?.tag ?? '';
        })
        .map((item: any) => String(item || '').trim())
        .filter(Boolean);
    }
    if (typeof source === 'string') {
      return source.split(',').map((tag: string) => tag.trim()).filter(Boolean);
    }
    return [];
  };

  const renderApprovalComments = (comments?: string) => {
    const raw = String(comments || '').trim();
    if (!raw) return '';

    const tokenRegex = /(username|userId)\s*[=:]\s*([^\s,;，；]+)/gi;
    const nodes: Array<string | any> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = tokenRegex.exec(raw)) !== null) {
      const [fullMatch, key, value] = match;
      const start = match.index;

      if (start > lastIndex) {
        nodes.push(raw.slice(lastIndex, start));
      }

      const normalizedKey = String(key || '').toLowerCase();
      const rawValue = String(value || '').trim();
      const displayName = normalizedKey === 'userid' ? getUserDisplayName(rawValue) : rawValue;

      nodes.push(
        <span
          key={`delegated-user-${start}`}
          className="inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold"
        >
          {displayName}
        </span>
      );

      lastIndex = start + fullMatch.length;
    }

    if (lastIndex < raw.length) {
      nodes.push(raw.slice(lastIndex));
    }

    return nodes.length > 0 ? nodes : raw;
  };

  const handleDownload = async (fileUrl?: string, version?: string) => {
    if (!document?.id) {
      notify('文档ID无效，无法下载', { type: 'warning' });
      return;
    }

    try {
      if (fileUrl) {
        const link = window.document.createElement('a');
        link.href = fileUrl;
        link.download = `${document.documentNumber}_v${version || document.currentVersion}`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        return;
      }

      // If no direct fileUrl, call backend download API (mirrors DocumentManageDetail / DocumentManageList behavior)
      const { downloadDocumentFile } = await import('../../../services/documentService.ts');
      await downloadDocumentFile(document.id, `${document.documentNumber}_v${document.currentVersion}`);
    } catch (err: any) {
      console.error('Failed to download file:', err);
      notify(`Download failed: ${err?.message || 'Unknown error'}`, { type: 'error' });
    }
  };

  const openDeputyModal = (stepInstanceId: string, fromUserId: string) => {
    setSelectedStepInstanceId(String(stepInstanceId));
    setSelectedFromUserId(String(fromUserId));
    setSelectedDeputyUserId('');
    setDeputyComment('');
    setShowDeputyModal(true);
  };

  const handleAssignDeputy = async () => {
    if (!id) return;
    if (!selectedStepInstanceId) {
      notify('Step instance not found', { type: 'warning' });
      return;
    }
    if (!selectedFromUserId) {
      notify('Original approver not found', { type: 'warning' });
      return;
    }
    if (!selectedDeputyUserId) {
      notify('Please select deputy approver', { type: 'warning' });
      return;
    }

    setSavingDeputy(true);
    try {
      await replaceApproverWithDeputy(
        selectedStepInstanceId,
        id,
        selectedFromUserId,
        selectedDeputyUserId,
        deputyComment
      );
      notify('Deputy approver assigned successfully', { type: 'success' });

      const refreshedApproval = await getDocumentApprovalInfo(id);
      setDocument((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          approvalHistory: refreshedApproval.length > 0 ? refreshedApproval : prev.approvalHistory,
        };
      });

      setShowDeputyModal(false);
      setSelectedStepInstanceId('');
      setSelectedFromUserId('');
      setSelectedDeputyUserId('');
      setDeputyComment('');
    } catch (assignError: any) {
      notify(assignError?.message || 'Failed to assign deputy approver', { type: 'error' });
    } finally {
      setSavingDeputy(false);
    }
  };

  return (
    <div className="px-10 pt-5 pb-6 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="mb-5 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to List
        </button>

        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              {getStatusIcon(document.status)}
              <h2 className="text-[22px] font-extrabold text-slate-900 leading-none">{document.title}</h2>
            </div>
            <div className="text-gray-600 mb-2">{document.description}</div>
            <div className="flex items-center gap-4 text-gray-500">
              <span>Document Number: {document.documentNumber}</span>
              <span>•</span>
              <span>Current Version: v{document.currentVersion}</span>
              <span>•</span>
              <span className={`px-2 py-0.5 rounded ${getStatusColor(document.status)}`}>
                {document.status === 'Draft' && 'Draft'}
                {document.status === 'Review' && 'Under Review'}
                {document.status === 'Approved' && 'Approved'}
                {document.status === 'Obsolete' && 'Obsolete'}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => handleDownload(document.versions.find(v => v.isActive)?.fileUrl, document.currentVersion)}
              className="inline-flex items-center px-3.5 h-9 bg-slate-900 text-white rounded-[10px] hover:bg-slate-800 transition-colors font-bold text-sm"
            >
              Download
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <h3 className="text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-gray-500 mb-1">Category</div>
                <div className="text-gray-900">
                  <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
                    {document.category}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Owner</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {getOwnerDisplayName()}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Description</div>
                <div className="text-gray-900 whitespace-pre-wrap break-words">{document.description || '-'}</div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Tags</div>
                <div className="flex flex-wrap gap-2">
                  {getDisplayTags().length === 0 && <span className="text-gray-500">-</span>}
                  {getDisplayTags().map((tag, index) => (
                    <span
                      key={index}
                      className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Created By</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {getUserDisplayName(document.createdBy)}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Created Date</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {new Date(document.createdAt).toLocaleString('en-US')}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Updated By</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-400" />
                  {getUserDisplayName(document.updatedBy)}
                </div>
              </div>
              <div>
                <div className="text-gray-500 mb-1">Last Updated</div>
                <div className="text-gray-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  {new Date(document.updatedAt).toLocaleString('en-US')}
                </div>
              </div>
            </div>
          </div>

          {/* Version History */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <GitBranch className="w-5 h-5 text-gray-700" />
              <h3 className="text-gray-900 text-lg font-semibold">Version History</h3>
            </div>
            <div className="space-y-4">
              {(document.versions || []).map((version, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border transition-all ${
                      version.isActive
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="text-base font-semibold text-gray-900">
                          Version {version.version}
                        </span>
                        {version.isActive && (
                          <span className="px-2.5 py-0.5 bg-blue-600 text-white rounded-full text-xs font-medium">
                            Current
                          </span>
                        )}
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(version.status)}`}>
                          {version.status === 'Draft' && 'Draft'}
                          {version.status === 'Review' && 'Under Review'}
                          {version.status === 'Approved' && 'Approved'}
                          {version.status === 'Obsolete' && 'Obsolete'}
                        </span>
                      </div>
                      {version.fileUrl && (
                        <button
                          onClick={() => handleDownload(version.fileUrl, version.version)}
                          className="flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </button>
                      )}
                    </div>
                    {version.changes && (
                      <div className="mb-3 pb-3 border-b border-gray-200">
                        <div className="text-gray-700 text-sm leading-relaxed">{version.changes}</div>
                      </div>
                    )}
                    <div className="flex items-center gap-4 text-gray-600 text-sm">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {new Date(version.createdAt).toLocaleString('en-US')}
                      </span>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <User className="w-3.5 h-3.5" />
                        {getUserDisplayName(version.createdBy)}
                      </span>
                      {version.fileSize && (
                        <>
                          <span>•</span>
                          <span>{formatFileSize(version.fileSize)}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              {(document.versions || []).length === 0 && (
                <div className="p-4 rounded-lg border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-600">
                  No version history available.
                </div>
              )}
            </div>
          </div>

          {/* Approval History */}
          {(document.approvalHistory || []).length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Approval History</h3>
              <div className="space-y-3">
                {document.approvalHistory.map((record) => (
                  <div key={record.id} className="p-4 border border-gray-200 rounded-lg">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{getUserDisplayName(record.approver)}</span>
                        <span className="text-gray-500">•</span>
                        <span className="text-gray-600 text-sm">Level {record.level}</span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            record.action === 'Approved'
                              ? 'bg-green-100 text-green-700'
                              : record.action === 'Rejected'
                              ? 'bg-red-100 text-red-700'
                              : record.action === 'Deputy'
                              ? 'bg-indigo-100 text-indigo-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {record.action === 'Approved' && 'Approved'}
                          {record.action === 'Rejected' && 'Rejected'}
                          {record.action === 'Deputy' && 'DEPUTY'}
                          {record.action === 'Pending' && 'Pending'}
                        </span>
                      </div>
                      <span className="text-gray-500 text-sm flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {new Date(record.timestamp).toLocaleString('en-US')}
                      </span>
                    </div>
                    {record.action === 'Pending' && (
                      <div className="mb-2">
                        <button
                          onClick={() => openDeputyModal(record.stepInstanceId || '', record.approverId || record.approver)}
                          disabled={!record.stepInstanceId}
                          className="text-xs px-2.5 py-1 border border-indigo-300 text-indigo-700 rounded hover:bg-indigo-50 transition-colors"
                        >
                          Assign Deputy
                        </button>
                      </div>
                    )}
                    {record.comments && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-gray-700 text-sm">
                          <span className="font-medium">Comments:</span> {renderApprovalComments(record.comments)}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(document.approvalHistory || []).length === 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-gray-900 text-lg font-semibold mb-4">Approval History</h3>
              <div className="text-sm text-gray-600">No approval history available.</div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Info */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
            <h4 className="text-gray-900 font-semibold mb-3">Document Status</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Lifecycle</span>
                <span className={`px-2 py-0.5 rounded ${getStatusColor(document.status)}`}>
                  {document.status === 'Draft' && 'Draft'}
                  {document.status === 'Review' && 'Under Review'}
                  {document.status === 'Approved' && 'Approved'}
                  {document.status === 'Obsolete' && 'Obsolete'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Versions</span>
                <span className="text-gray-900">{(document.versions || []).length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Approval Records</span>
                <span className="text-gray-900">{(document.approvalHistory || []).length}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
            <h4 className="text-gray-900 font-semibold mb-3">Actions</h4>
            <div className="space-y-2">
              <button
                onClick={() => navigate(-1)}
                className="w-full px-3 py-2 text-left text-gray-700 hover:bg-gray-50 rounded transition-colors"
              >
                Back to Task List
              </button>
              {(document.versions || []).some(v => v.isActive && v.fileUrl) && (
                <button
                  onClick={() => handleDownload(document.versions.find(v => v.isActive)?.fileUrl, document.currentVersion)}
                  className="w-full px-3 py-2 text-left text-blue-600 hover:bg-blue-50 rounded transition-colors block"
                >
                  Download Document
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showDeputyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-xl w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">Assign Deputy Approver</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Deputy User *
                </label>
                <select
                  value={selectedDeputyUserId}
                  onChange={(e) => setSelectedDeputyUserId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={savingDeputy}
                >
                  <option value="">-- Select user --</option>
                  {allUsers.map((u: any) => {
                    const userId = u?.id ?? u?.Id ?? u?.userId;
                    const userName = u?.userName ?? u?.username ?? u?.name;
                    if (userId == null) return null;
                    return (
                      <option key={String(userId)} value={String(userId)}>
                        {String(userName || userId)}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comment (Optional)
                </label>
                <textarea
                  value={deputyComment}
                  onChange={(e) => setDeputyComment(e.target.value)}
                  placeholder="Reason for deputy assignment"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  disabled={savingDeputy}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeputyModal(false);
                  setSelectedStepInstanceId('');
                  setSelectedFromUserId('');
                  setSelectedDeputyUserId('');
                  setDeputyComment('');
                }}
                disabled={savingDeputy}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAssignDeputy}
                disabled={savingDeputy || !selectedDeputyUserId}
                className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingDeputy ? 'Saving...' : 'Confirm Deputy'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
